// A fixed Intertown route names a town, and a town's centre never moves, yet the
// page used to ask Google to geocode it on every Intertown tab open and every
// route tap. Resolve it once per device instead and reuse it.
//
// Deliberately NOT inside forwardGeocode: Book's search shares that function
// with different options, so caching there could change other screens.

const CACHE_PREFIX = 'mt_intertown_town_v1:';
export const TOWN_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
// Same box the Intertown map picker uses; anything outside it is not a town we serve.
const INDIA = { north: 38.5, south: 6.0, west: 68.0, east: 98.5 };

const defaultStorage = () => {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
};
const defaultMemory = new Map();
const defaultInflight = new Map();

export const townKey = (name) => String(name || '').trim().toLowerCase();

const isUsablePlace = (place) => {
  const lat = Number(place?.lat);
  const lng = Number(place?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= INDIA.south && lat <= INDIA.north && lng >= INDIA.west && lng <= INDIA.east;
};

const plainPlace = (place) => ({
  address: place.address,
  name: place.name,
  lat: Number(place.lat),
  lng: Number(place.lng),
});

const readStored = (storage, key, nowMs) => {
  try {
    const raw = storage?.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    const age = nowMs - Number(entry?.at);
    if (!Number.isFinite(age) || age < 0 || age > TOWN_CACHE_TTL_MS) return null;
    return isUsablePlace(entry.place) ? plainPlace(entry.place) : null;
  } catch {
    return null;
  }
};

const writeStored = (storage, key, place, nowMs) => {
  try { storage?.setItem(CACHE_PREFIX + key, JSON.stringify({ at: nowMs, place })); } catch { /* full or private mode */ }
};

/**
 * Resolves a town's centre, asking `geocode()` only when this device has no
 * fresh answer. Always returns a promise and a fresh copy, so callers can
 * treat it exactly like the geocoder it wraps. Failures are never cached.
 */
export async function resolveTown(name, {
  geocode, storage = defaultStorage(), now = Date.now, memory = defaultMemory, inflight = defaultInflight,
} = {}) {
  const key = townKey(name);
  if (!key) return geocode();
  if (memory.has(key)) return { ...memory.get(key) };

  const stored = readStored(storage, key, now());
  if (stored) {
    memory.set(key, stored);
    return { ...stored };
  }

  if (!inflight.has(key)) {
    inflight.set(key, (async () => {
      try {
        const place = await geocode();
        if (isUsablePlace(place)) {
          const plain = plainPlace(place);
          memory.set(key, plain);
          writeStored(storage, key, plain, now());
        }
        return place;
      } finally {
        inflight.delete(key);
      }
    })());
  }
  return { ...(await inflight.get(key)) };
}

/** Drops a remembered town, e.g. when the server rejected its location. */
export function forgetTown(name, {
  storage = defaultStorage(), memory = defaultMemory, inflight = defaultInflight,
} = {}) {
  const key = townKey(name);
  if (!key) return;
  memory.delete(key);
  inflight.delete(key);
  try { storage?.removeItem(CACHE_PREFIX + key); } catch { /* nothing to remove */ }
}

/** The server's refusal of a fixed-route destination that is not near that town. */
export const isDestinationOutsideRoute = (error) => error?.details?.reason === 'destination_outside_route';
